import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  checkFileExist,
  getAllRootFiles,
  getLargeFiles,
  moveFile,
  renameFiles,
  uploadFiles,
} from "store/services/files";
import { closeCenterModal } from "store/slices/CenterModal.slice";
import { openToast } from "store/slices/toast.slice";
import { getApiMessage } from "utils/apiErrorCode";
import { updateFileProgress } from "store/slices/general";
import {
  clearUploadController,
  setUploadController,
} from "utils/uploadController";

export const GetAllRootFilesAction = createAsyncThunk(
  "files/getAllRootFiles",
  async (payload, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      const { isLoadMore, ...rest } = payload;
      const response = await getAllRootFiles(rest);
      if (response.status === 1) {
        return fulfillWithValue({ isLoadMore, ...response });
      }
      dispatch(
        openToast({
          type: "error",
          message: getApiMessage(response?.statusCode),
        }),
      );
      return rejectWithValue(response);
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data?.statusCode
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

// export const UploadFilesAction = createAsyncThunk(
//   "files/uploadFiles",
//   async (payload, { rejectWithValue, dispatch, fulfillWithValue }) => {
//     const { onSuccess, onError, formData, tempIds } = payload;
//     try {
//       const controller = new AbortController();

//       tempIds?.forEach((tempId) => {
//         setUploadController(tempId, controller);
//       });

//       const response = await uploadFiles(
//         formData,
//         (progressEvent) => {
//           const percent = progressEvent.total
//             ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
//             : 0;

//           tempIds?.forEach((tempId) => {
//             dispatch(
//               updateFileProgress({
//                 tempId,
//                 progress: percent,
//               }),
//             );
//           });
//         },
//         controller.signal,
//       );

//       tempIds?.forEach(clearUploadController);

//       if (response.status === 1) {
//         onSuccess?.(response?.response);
//         return fulfillWithValue({ ...response, tempIds });
//       }

//       onError?.(response?.statusCode);
//       return rejectWithValue(response);
//     } catch (error) {
//       tempIds?.forEach(clearUploadController);

//       if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
//         return rejectWithValue({
//           canceled: true,
//           tempIds,
//         });
//       }

//       dispatch(
//         openToast({
//           type: "error",
//           message: error?.response?.data?.statusCode
//             ? getApiMessage(error?.response?.data?.statusCode)
//             : error?.message,
//         }),
//       );

//       return rejectWithValue(error);
//     }
//   },
// );

export const UploadFilesAction = createAsyncThunk(
  "files/uploadFiles",
  async (payload, { rejectWithValue, dispatch }) => {
    const { onSuccess, onError, formData, tempId, oldFileId } = payload;
    try {
      const controller = new AbortController();
      setUploadController(tempId, controller);

      const response = await uploadFiles(
        formData,
        (progressEvent) => {
          const percent = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;

          dispatch(updateFileProgress({ tempId, progress: percent }));
        },
        controller.signal,
      );

      clearUploadController(tempId);

      if (response.status === 1) {
        onSuccess?.(response?.response, tempId);
        return { ...response, tempId, oldFileId };
      }

      onError?.(response?.statusCode, tempId);
      return rejectWithValue({ ...response, tempId });
    } catch (error) {
      clearUploadController(tempId);

      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return rejectWithValue({ canceled: true, tempId });
      }

      if (onError) {
        const errorStatusCode = error?.response?.data?.statusCode || error?.response?.status;
        onError(errorStatusCode || error?.message, tempId);
      } else {
        dispatch(
          openToast({
            type: "error",
            message: error?.response?.data?.statusCode
              ? getApiMessage(error?.response?.data?.statusCode)
              : error?.message,
          }),
        );
      }

      return rejectWithValue({ ...error, tempId });
    }
  },
);
export const MoveFileAction = createAsyncThunk(
  "files/moveFile",
  async (payload, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      const { onSuccess, fileId, ...rest } = payload;
      const response = await moveFile({ fileId, ...rest });
      if (response.status === 1) {
        onSuccess?.();
        dispatch(closeCenterModal());
        return fulfillWithValue({ fileId });
      }
      dispatch(
        openToast({
          type: "error",
          message: getApiMessage(response?.statusCode),
        }),
      );
      return rejectWithValue(response);
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data?.statusCode
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const RenameFilesAction = createAsyncThunk(
  "files/renameFiles",
  async (payload, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      const { onSuccess, onError, ...rest } = payload;
      const response = await renameFiles(rest);
      if (response.status === 1) {
        onSuccess?.();
        dispatch(closeCenterModal());
        return fulfillWithValue(response);
      }
      onError?.(response);
      return rejectWithValue(response);
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data?.statusCode
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const GetLargeFileAction = createAsyncThunk(
  "files/getLargeFiles",
  async (payload, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      const { isLoadMore, ...rest } = payload;
      const response = await getLargeFiles(rest);
      if (response.status === 1) {
        return fulfillWithValue({ isLoadMore, ...response });
      }
      dispatch(
        openToast({
          type: "error",
          message: getApiMessage(response?.statusCode),
        }),
      );
      return rejectWithValue(response);
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data?.statusCode
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);


export const CheckFileExistAction = createAsyncThunk(
  "files/checkFileExist",
  async (payload, { rejectWithValue, dispatch, fulfillWithValue }) => {
    try {
      const { onSuccess, onError, ...rest } = payload;
      const response = await checkFileExist(rest);
      if (response.status === 1) {
        onSuccess?.();
        dispatch(closeCenterModal());
        return fulfillWithValue(response);
      }
      onError?.(response);
      return rejectWithValue(response);
    } catch (error) {
      dispatch(
        openToast({
          type: "error",
          message: error?.response?.data?.statusCode
            ? getApiMessage(error?.response?.data?.statusCode)
            : error?.message,
        }),
      );
      return rejectWithValue(error);
    }
  },
);
